import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const OrganizationProfileStep = ({ data, onComplete, onCancel }) => {
  const [formData, setFormData] = useState({
    companyName: data?.companyName || '',
    abnNumber: data?.abnNumber || '',
    tinNumber: data?.tinNumber || '',
    employeeCount: data?.employeeCount || '',
    industryType: data?.industryType || '',
    primaryLocation: data?.primaryLocation || '',
    contactEmail: data?.contactEmail || '',
    contactPhone: data?.contactPhone || ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const employeeCountOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-100', label: '51-100 employees' },
    { value: '101-250', label: '101-250 employees' },
    { value: '251-500', label: '251-500 employees' },
    { value: '501+', label: '501+ employees' }
  ];

  const industryOptions = [
    { value: 'mining', label: 'Mining & Resources' },
    { value: 'oil_gas', label: 'Oil & Gas' },
    { value: 'agriculture', label: 'Agriculture & Livestock' },
    { value: 'forestry', label: 'Forestry & Logging' },
    { value: 'fishing', label: 'Fishing & Aquaculture' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'construction', label: 'Construction' },
    { value: 'retail', label: 'Retail & Wholesale' },
    { value: 'hospitality', label: 'Hospitality & Tourism' },
    { value: 'transport', label: 'Transport & Logistics' },
    { value: 'telecommunications', label: 'Telecommunications' },
    { value: 'finance', label: 'Banking & Finance' },
    { value: 'professional_services', label: 'Professional Services' },
    { value: 'healthcare', label: 'Healthcare & Medical' },
    { value: 'education', label: 'Education & Training' },
    { value: 'government', label: 'Government & Public Sector' },
    { value: 'ngo', label: 'NGO & Non-Profit' },
    { value: 'other', label: 'Other' }
  ];

  const locationOptions = [
    { value: 'port_moresby', label: 'Port Moresby (National Capital District)' },
    { value: 'lae', label: 'Lae (Morobe Province)' },
    { value: 'mount_hagen', label: 'Mount Hagen (Western Highlands)' },
    { value: 'madang', label: 'Madang (Madang Province)' },
    { value: 'kokopo', label: 'Kokopo (East New Britain)' },
    { value: 'goroka', label: 'Goroka (Eastern Highlands)' },
    { value: 'wewak', label: 'Wewak (East Sepik)' },
    { value: 'kimbe', label: 'Kimbe (West New Britain)' },
    { value: 'other', label: 'Other Location' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData?.employeeCount) {
      newErrors.employeeCount = 'Employee count is required';
    }

    if (!formData?.industryType) {
      newErrors.industryType = 'Industry type is required';
    }

    if (!formData?.primaryLocation) {
      newErrors.primaryLocation = 'Primary location is required';
    }

    if (!formData?.contactEmail?.trim()) {
      newErrors.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      try {
        setSaving(true);
        
        // Save organization profile to company_settings
        const { data: companyData, error: companyError } = await supabase?.from('company_settings')?.insert({
            company_name: formData?.companyName,
            company_legal_name: formData?.companyName,
            contact_email: formData?.contactEmail,
            contact_phone: formData?.contactPhone,
            tax_identification_number: formData?.tinNumber,
            business_registration_number: formData?.abnNumber,
            industry: formData?.industryType,
            company_size: formData?.employeeCount
          })?.select()?.single();

        if (companyError) throw companyError;

        onComplete?.({
          ...formData,
          companyId: companyData?.id
        });
      } catch (err) {
        console.error('Error saving organization profile:', err);
        setErrors({ submit: err?.message || 'Failed to save organization profile' });
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <Icon name="Building2" size={24} className="text-emerald-600 mr-2" />
          <h2 className="text-2xl font-bold text-gray-900">Organization Profile</h2>
        </div>
        <p className="text-gray-600">
          Enter your organization details. We'll auto-validate ABN/TIN numbers for registered PNG businesses.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error Display */}
        {errors?.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <Icon name="AlertCircle" size={20} className="text-red-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">Error</p>
              <p className="text-sm text-red-700 mt-1">{errors?.submit}</p>
            </div>
          </div>
        )}

        {/* Company Name */}
        <Input
          label="Company Name"
          required
          value={formData?.companyName}
          onChange={(e) => handleChange('companyName', e?.target?.value)}
          error={errors?.companyName}
          placeholder="e.g., PNG Resources Ltd"
        />

        {/* ABN/TIN Numbers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="ABN Number (Optional)"
            value={formData?.abnNumber}
            onChange={(e) => handleChange('abnNumber', e?.target?.value)}
            placeholder="e.g., 12345678"
            description="Australian Business Number if applicable"
          />
          <Input
            label="TIN Number (Optional)"
            value={formData?.tinNumber}
            onChange={(e) => handleChange('tinNumber', e?.target?.value)}
            placeholder="e.g., 987654321"
            description="Tax Identification Number"
          />
        </div>

        {/* Employee Count */}
        <Select
          label="Employee Count"
          required
          options={employeeCountOptions}
          value={formData?.employeeCount}
          onChange={(value) => handleChange('employeeCount', value)}
          error={errors?.employeeCount}
          placeholder="Select employee count range"
        />

        {/* Industry Type */}
        <Select
          label="Industry Type"
          required
          options={industryOptions}
          value={formData?.industryType}
          onChange={(value) => handleChange('industryType', value)}
          error={errors?.industryType}
          placeholder="Select your industry"
          searchable
        />

        {/* Primary Location */}
        <Select
          label="Primary Location"
          required
          options={locationOptions}
          value={formData?.primaryLocation}
          onChange={(value) => handleChange('primaryLocation', value)}
          error={errors?.primaryLocation}
          placeholder="Select primary location"
          searchable
        />

        {/* Contact Information */}
        <div className="border-t pt-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
          
          <div className="space-y-4">
            <Input
              label="Contact Email"
              type="email"
              required
              value={formData?.contactEmail}
              onChange={(e) => handleChange('contactEmail', e?.target?.value)}
              error={errors?.contactEmail}
              placeholder="admin@company.com.pg"
            />

            <Input
              label="Contact Phone (Optional)"
              type="tel"
              value={formData?.contactPhone}
              onChange={(e) => handleChange('contactPhone', e?.target?.value)}
              placeholder="+675 XXX XXXX"
            />
          </div>
        </div>

        {/* Interactive Organization Structure Builder Info */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="Info" size={20} className="text-emerald-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">Organization Structure</p>
              <p className="text-sm text-emerald-700 mt-1">
                After completing this step, you'll be able to create departments with drag-and-drop hierarchy setup and role assignment templates.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            iconName="X"
            disabled={saving}
          >
            Cancel Setup
          </Button>
          <Button
            type="submit"
            iconName="ArrowRight"
            iconPosition="right"
            loading={saving}
          >
            Continue to Bank Integration
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OrganizationProfileStep;